import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  HostListener,
  NgZone
} from '@angular/core';
import * as THREE from 'three';

const vertexShader = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 1.0);
}
`;

const fragmentShader = `
precision highp float;

uniform float iTime;
uniform vec3 iResolution;
uniform vec3 uColor;
uniform float uAmplitude;
uniform float uDistance;
uniform vec2 uMouse;

#define PI 3.1415926538

const int u_line_count = 40;
const float u_line_width = 7.0;
const float u_line_blur = 10.0;

float Perlin2D(vec2 P) {
    vec2 Pi = floor(P);
    vec4 Pf_Pfmin1 = P.xyxy - vec4(Pi, Pi + 1.0);
    vec4 Pt = vec4(Pi.xy, Pi.xy + 1.0);
    Pt = Pt - floor(Pt * (1.0 / 71.0)) * 71.0;
    Pt += vec2(26.0, 161.0).xyxy;
    Pt *= Pt;
    Pt = Pt.xzxz * Pt.yyww;
    vec4 hash_x = fract(Pt * (1.0 / 951.135664));
    vec4 hash_y = fract(Pt * (1.0 / 642.949883));
    vec4 grad_x = hash_x - 0.49999;
    vec4 grad_y = hash_y - 0.49999;
    vec4 grad_results = inversesqrt(grad_x * grad_x + grad_y * grad_y)
        * (grad_x * Pf_Pfmin1.xzxz + grad_y * Pf_Pfmin1.yyww);
    grad_results *= 1.4142135623730950;
    vec2 blend = Pf_Pfmin1.xy * Pf_Pfmin1.xy * Pf_Pfmin1.xy
               * (Pf_Pfmin1.xy * (Pf_Pfmin1.xy * 6.0 - 15.0) + 10.0);
    vec4 blend2 = vec4(blend, vec2(1.0 - blend));
    return dot(grad_results, blend2.zxzx * blend2.wwyy);
}

float pixel(float count, vec2 resolution) {
    return (1.0 / max(resolution.x, resolution.y)) * count;
}

float lineFn(vec2 st, float width, float perc, float offset, vec2 mouse, float time, float amplitude, float distance) {
    float split_offset = (perc * 0.4);
    float split_point = 0.1 + split_offset;

    float amplitude_normal = smoothstep(split_point, 0.7, st.x);
    float amplitude_strength = 0.5;
    float finalAmplitude = amplitude_normal * amplitude_strength
                           * amplitude * (1.0 + (mouse.y - 0.5) * 0.2);

    float time_scaled = time / 10.0 + (mouse.x - 0.5) * 1.0;
    float blur = smoothstep(split_point, split_point + 0.05, st.x) * perc;

    float xnoise = mix(
        Perlin2D(vec2(time_scaled, st.x + perc) * 2.5),
        Perlin2D(vec2(time_scaled, st.x + time_scaled) * 3.5) / 1.5,
        st.x * 0.3
    );

    float y = 0.5 + (perc - 0.5) * distance + xnoise / 2.0 * finalAmplitude;

    float line_start = smoothstep(
        y + (width / 2.0) + (u_line_blur * pixel(1.0, iResolution.xy) * blur),
        y,
        st.y
    );

    float line_end = smoothstep(
        y,
        y - (width / 2.0) - (u_line_blur * pixel(1.0, iResolution.xy) * blur),
        st.y
    );

    return clamp(
        (line_start - line_end) * (1.0 - smoothstep(0.0, 1.0, pow(perc, 0.3))),
        0.0,
        1.0
    );
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
    vec2 uv = fragCoord / iResolution.xy;

    float line_strength = 1.0;
    for (int i = 0; i < u_line_count; i++) {
        float p = float(i) / float(u_line_count);
        line_strength *= (1.0 - lineFn(
            uv,
            u_line_width * pixel(1.0, iResolution.xy) * (1.0 - p),
            p,
            (PI * 1.0) * p,
            uMouse,
            iTime,
            uAmplitude,
            uDistance
        ));
    }

    float colorVal = 1.0 - line_strength;
    fragColor = vec4(uColor * colorVal, colorVal);
}

void main() {
    mainImage(gl_FragColor, gl_FragCoord.xy);
}
`;

@Component({
  selector: 'nc-threads-background',
  templateUrl: './threads-background.component.html',
  styleUrls: ['./threads-background.component.less']
})
export class ThreadsBackgroundComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('container', { static: true }) containerRef!: ElementRef<HTMLDivElement>;

  /** 颜色 [r, g, b] 数组（0-1） */
  @Input() ncColor: number[] = [1, 1, 1];
  /** 振幅大小（0-5） */
  @Input() ncAmplitude: number = 1;
  /** 线条距离（0-2） */
  @Input() ncDistance: number = 0;
  /** 背景颜色 */
  @Input() ncBgColor: string = 'black';

  /** 是否启用鼠标交互 */
  private _enableMouseInteraction: boolean = false;
  @Input()
  set ncEnableMouseInteraction(val: boolean | string) {
    this._enableMouseInteraction = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncEnableMouseInteraction(): boolean {
    return this._enableMouseInteraction;
  }

  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.OrthographicCamera;
  private material!: THREE.ShaderMaterial;
  private geometry!: THREE.PlaneGeometry;
  private mesh!: THREE.Mesh;

  private resizeObserver!: ResizeObserver;
  private intersectionObserver!: IntersectionObserver;
  private animationFrameId: number | null = null;
  private isVisible = true;
  private isRunning = false;

  private currentMouse = new THREE.Vector2(0.5, 0.5);
  private targetMouse = new THREE.Vector2(0.5, 0.5);

  constructor(private ngZone: NgZone) { }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.initWebGL();
    });
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.material) {
      if (changes['ncColor']) {
        this.material.uniforms['uColor'].value.copy(this.getThreeColor(this.ncColor));
      }
      if (changes['ncAmplitude']) {
        this.material.uniforms['uAmplitude'].value = this.ncAmplitude;
      }
      if (changes['ncDistance']) {
        this.material.uniforms['uDistance'].value = this.ncDistance;
      }
    }
  }

  private getThreeColor(color: number[]): THREE.Color {
    if (Array.isArray(color)) {
      return new THREE.Color(color[0], color[1], color[2]);
    }
    return new THREE.Color(color);
  }

  private initWebGL(): void {
    const container = this.containerRef.nativeElement;

    // 初始化渲染器
    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
    this.renderer.setClearColor(0x000000, 0);
    // 采用与 Threads.jsx 中 blendFunc(SRC_ALPHA, ONE_MINUS_SRC_ALPHA) 一致的默认混合方式
    container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    this.geometry = new THREE.PlaneGeometry(2, 2);

    // 初始分辨率设定，后续由 resize 更新
    const canvasWidth = container.clientWidth || 1;
    const canvasHeight = container.clientHeight || 1;

    this.material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      transparent: true,
      uniforms: {
        iTime: { value: 0 },
        iResolution: { value: new THREE.Vector3(canvasWidth, canvasHeight, canvasWidth / canvasHeight) },
        uColor: { value: this.getThreeColor(this.ncColor) },
        uAmplitude: { value: this.ncAmplitude },
        uDistance: { value: this.ncDistance },
        uMouse: { value: new THREE.Vector2(0.5, 0.5) }
      }
    });

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.scene.add(this.mesh);

    this.setupResizeObserver(container);
    this.setupIntersectionObserver(container);

    this.resize();
    this.start();
  }

  private setupResizeObserver(container: HTMLElement): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.ngZone.runOutsideAngular(() => {
        this.resize();
      });
    });
    this.resizeObserver.observe(container);
  }

  private setupIntersectionObserver(container: HTMLElement): void {
    this.intersectionObserver = new IntersectionObserver(entries => {
      const entry = entries[0];
      this.isVisible = entry.isIntersecting && entry.intersectionRatio > 0;
      if (this.isVisible && !document.hidden) {
        this.ngZone.runOutsideAngular(() => {
          this.start();
        });
      } else {
        this.pause();
      }
    }, { threshold: [0, 0.01, 0.1] });
    this.intersectionObserver.observe(container);

    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private onVisibilityChange = () => {
    if (document.hidden) {
      this.pause();
    } else if (this.isVisible) {
      this.ngZone.runOutsideAngular(() => {
        this.start();
      });
    }
  };

  private resize(): void {
    if (!this.containerRef || !this.renderer) return;
    const container = this.containerRef.nativeElement;
    const width = container.clientWidth;
    const height = container.clientHeight;

    this.renderer.setSize(width, height);
    // 可选设置 pixelRatio，如不需要极高精度可保留 1 提高性能
    // this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    this.material.uniforms['iResolution'].value.set(
      width,
      height,
      (width / Math.max(height, 1))
    );
  }

  private update = (t: number): void => {
    if (!this.isRunning) return;

    if (this.ncEnableMouseInteraction) {
      const smoothing = 0.05;
      this.currentMouse.x += smoothing * (this.targetMouse.x - this.currentMouse.x);
      this.currentMouse.y += smoothing * (this.targetMouse.y - this.currentMouse.y);
      this.material.uniforms['uMouse'].value.copy(this.currentMouse);
    } else {
      this.material.uniforms['uMouse'].value.set(0.5, 0.5);
    }

    this.material.uniforms['iTime'].value = t * 0.001;

    this.renderer.render(this.scene, this.camera);
    this.animationFrameId = requestAnimationFrame(this.update);
  };

  private start(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.animationFrameId = requestAnimationFrame(this.update);
  }

  private pause(): void {
    this.isRunning = false;
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private cleanup(): void {
    this.pause();
    try { this.resizeObserver?.disconnect(); } catch (e) { void 0; }
    try { this.intersectionObserver?.disconnect(); } catch (e) { void 0; }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    if (this.renderer) {
      const canvas = this.renderer.domElement;
      if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
      this.renderer.dispose();
      this.renderer.forceContextLoss();
    }
    if (this.geometry) this.geometry.dispose();
    if (this.material) this.material.dispose();
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.ncEnableMouseInteraction || !this.containerRef) return;
    const container = this.containerRef.nativeElement;
    const rect = container.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = 1.0 - (event.clientY - rect.top) / rect.height;
    this.targetMouse.set(x, y);
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (!this.ncEnableMouseInteraction) return;
    this.targetMouse.set(0.5, 0.5);
  }
}
